import '../css/main.scss';

import { apiAnalytics } from '@api/analytics';
import { apiUser } from '@api/user';
import PromoCode from '@components/PromoCode/PromoCode.vue';
import { BeliefsSection, CenteredStatementSection, VisionSection } from '@components/about';
import { AuthPage } from '@components/auth';
import {
  BaseAccordion,
  BaseAccordionItem,
  BaseDialog,
  BaseHeader,
  BaseMasonry,
  BaseMenu,
  BaseSelect,
  BaseSticky,
} from '@components/base';
import {
  BaseAccordionRedesign,
} from '@components/base/redesign';
import { CheckoutSuccessListener } from '@components/common/index';
import CommunicationPreferences from '@components/communication-preferences/CommunicationPreferences.vue';
import ConsentBanner from '@components/consent-banner/ConsentBanner.vue';
import EmailPopup from '@components/email-popup/EmailPopup.vue';
import FirstStepHero from '@components/first-step-hero/FirstStepHero.vue';
import { SiteHero } from '@components/hero';
import { AdvantagesSection, FirstSection, LastSection } from '@components/how-it-works';
import MainLogo from '@components/main-logo/MainLogo.vue';
import { LoginModal, LoginPwaModal, ProductModal, ResetPasswordModal } from '@components/modals';
import { NavigationMenu } from '@components/navigation-menu';
import {
  PricingApproachSection,
  PricingCta1Section,
  PricingCta2Section,
  PricingFaqSection,
  PricingPlansSection,
  PricingVoiceSection,
  PricingWhyJrnysSection,
} from '@components/pricing';
import { QuizDevRoot, QuizIconCard, QuizRoot } from '@components/quiz';
import BaseDialogV2 from '@components/quiz_v2/BaseDialog/BaseDialog.vue';
import LoginModalQuiz from '@components/quiz_v2/LoginModal/LoginModal.vue';
import ProductModalQuiz from '@components/quiz_v2/ProductModal/ProductModal.vue';
import ResetPasswordModalQuiz from '@components/quiz_v2/ResetPasswordModal/ResetPasswordModal.vue';
import { CardSuccesses, OffsetGrid } from '@components/reviews';
import { SiteHeader } from '@components/site-header';
import ToastNotifications from '@components/toast-notifications/ToastNotifications.vue';
import { usePostHog } from '@composables/usePostHog';
import { AUTH_ROUTES } from '@constants/auth-routes';
import { getSafeLoginReturnUrl } from '@helpers/auth-redirect';

import { initTrustArc } from '@helpers/trustarc';
import { getLegacyUserMenuRoleKey, getPostLoginRedirectUrl, getUserMenuItemsForSession } from '@helpers/user-menu';
import type { MenuItem } from '@models/menu';
import { MODAL_TYPE } from '@models/modals';
import * as Sentry from '@sentry/vue';
import { useAnalyticsStore, useQuizStore, useUserStore } from '@stores/index';
import MasonryWall from '@yeger/vue-masonry-wall';
// @ts-ignore: No types
import vClickOutside from 'click-outside-vue3';
import { createPinia, mapStores } from 'pinia';
import { register } from 'swiper/element/bundle';
import { createApp } from 'vue';
// @ts-ignore: No types
import ToastPlugin from 'vue-toast-notification';
import 'core-js/actual/array/find-last-index';
import 'core-js/actual/string/replace-all';
import '@helpers/footerForm';

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $root: {
      modals: { [type: string]: boolean }
      modalPayload: { [type: string]: any }
      setModal: (type: MODAL_TYPE, visible: boolean, payload?: any) => void
      userMenuItems: MenuItem[]
    };
  }
}

usePostHog();
register();

function initSwiperNavigationButtonVisibility() {
  document.querySelectorAll('.card-swiper').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;

    const updateNavigationVisibility = () => {
      const swiper = (el as any).swiper;
      if (!swiper) return;

      const progress = Number(swiper.progress ?? 0);
      const isBeginning = Boolean(swiper.isBeginning) || progress <= 0.001;
      const isEnd = Boolean(swiper.isEnd) || progress >= 0.999;

      el.classList.toggle('at-start', isBeginning);
      el.classList.toggle('at-end', isEnd);
    };

    el.addEventListener('swiperinit', updateNavigationVisibility);
    el.addEventListener('swiperslidechange', updateNavigationVisibility);
    el.addEventListener('swiperprogress', updateNavigationVisibility);
    el.addEventListener('swiperreachend', updateNavigationVisibility);
    el.addEventListener('swiperfromedge', updateNavigationVisibility);
    el.addEventListener('swiperresize', updateNavigationVisibility);

    // Set initial state (swiper may not be ready yet, retry until it is or max 50 attempts)
    let attempts = 0;
    const setInitialState = () => {
      updateNavigationVisibility();
      if (!(el as any).swiper && attempts < 50) {
        attempts += 1;
        requestAnimationFrame(setInitialState);
      }
    };
    requestAnimationFrame(setInitialState);
  });
}

function initProductSwiperDragGuard() {
  document.querySelectorAll('swiper-container.card-swiper, swiper-container.products-swiper').forEach((el) => {
    let startX = 0;
    let startY = 0;
    let suppressNextClick = false;
    let pointerDown = false;
    const dragThreshold = 8;

    el.addEventListener('pointerdown', (event) => {
      const e = event as PointerEvent;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointerDown = true;
      suppressNextClick = false;
      startX = e.clientX;
      startY = e.clientY;
    });

    el.addEventListener('pointermove', (event) => {
      if (!pointerDown) return;
      const e = event as PointerEvent;
      if (Math.abs(e.clientX - startX) > dragThreshold || Math.abs(e.clientY - startY) > dragThreshold) {
        suppressNextClick = true;
      }
    });

    const onPointerDone = () => {
      pointerDown = false;
      window.setTimeout(() => {
        suppressNextClick = false;
      }, 0);
    };

    el.addEventListener('pointerup', onPointerDone);
    el.addEventListener('pointercancel', onPointerDone);

    // Card overlays are full-slide links; cancel click after drag so swipe wins.
    el.addEventListener('click', (event) => {
      if (!suppressNextClick) return;
      const target = event.target as Element | null;
      if (target?.closest('a')) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);
  });
}

function initProductSwiperDirectionGuard() {
  document.querySelectorAll<any>('swiper-container.card-swiper, swiper-container.products-swiper').forEach((el) => {
    const applyLtr = () => {
      const swiper = el.swiper;
      if (!swiper) return;
      swiper.changeLanguageDirection?.('ltr');
      swiper.params.allowSlidePrev = true;
      swiper.params.allowSlideNext = true;
      swiper.update();
    };

    el.style.direction = 'ltr';
    el.addEventListener('swiperinit', applyLtr);
    requestAnimationFrame(applyLtr);
  });
}

function initProductSwiperManualSwipeFallback() {
  document.querySelectorAll<any>('swiper-container.card-swiper, swiper-container.products-swiper').forEach((el) => {
    let startX = 0;
    let startY = 0;
    let active = false;
    const minDistance = 24;
    const maxVerticalDrift = 40;

    el.addEventListener('pointerdown', (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      active = true;
      startX = event.clientX;
      startY = event.clientY;
    }, { capture: true });

    el.addEventListener('pointerup', (event: PointerEvent) => {
      if (!active) return;
      active = false;

      const swiper = el.swiper;
      if (!swiper || swiper.destroyed || !swiper.enabled) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      // Horizontal swipe fallback when native swipe is intercepted.
      if (Math.abs(dy) > maxVerticalDrift || Math.abs(dx) < minDistance) return;

      if (dx < 0 && !swiper.isEnd) {
        swiper.slideNext();
      } else if (dx > 0 && !swiper.isBeginning) {
        swiper.slidePrev();
      }
    }, { capture: true });

    el.addEventListener('pointercancel', () => {
      active = false;
    }, { capture: true });
  });
}

function initProductSwiperWheelScroll() {
  document.querySelectorAll<any>('swiper-container.card-swiper, swiper-container.products-swiper').forEach((el) => {
    let wheelLocked = false;
    let wheelAccumulator = 0;
    const smoothSpeed = 380;
    const activationThreshold = 14;

    el.addEventListener('wheel', (event: WheelEvent) => {
      const swiper = el.swiper;
      if (!swiper || swiper.destroyed || !swiper.enabled) return;
      swiper.params.speed = smoothSpeed;

      // Prefer true horizontal deltas; allow Shift+wheel fallback for mice.
      const horizontalIntent = Math.abs(event.deltaX) >= Math.abs(event.deltaY) * 0.5;
      let primaryDelta = horizontalIntent ? event.deltaX : 0;
      if (!horizontalIntent && event.shiftKey) primaryDelta = event.deltaY;
      if (Math.abs(primaryDelta) < 1) return;

      event.preventDefault();
      if (wheelLocked || swiper.animating) return;

      wheelAccumulator += primaryDelta;
      if (Math.abs(wheelAccumulator) < activationThreshold) return;

      const wantsNext = wheelAccumulator > 0;
      wheelAccumulator = 0;
      if ((wantsNext && swiper.isEnd) || (!wantsNext && swiper.isBeginning)) return;

      wheelLocked = true;

      if (wantsNext) swiper.slideNext(smoothSpeed);
      else swiper.slidePrev(smoothSpeed);

      window.setTimeout(() => {
        wheelLocked = false;
      }, 80);
    }, { passive: false });
  });
}

function initProductCategoriesTabs() {
  const container = document.querySelector('[data-product-categories-tabs]');
  if (!container) return;

  const tabs = container.querySelectorAll<HTMLElement>('.product-categories-tabs__tab');
  const sections = document.querySelectorAll<HTMLElement>('[data-product-section]');

  const bgActive = 'bg-off-black-500';
  const bgInactive = 'bg-white';
  const textActive = 'text-white';
  const textInactive = 'text-off-black-500';

  function applyFilter(filter: string) {
    tabs.forEach((t) => {
      const slug = t.getAttribute('data-category-filter');
      const isActive = slug === filter;
      t.classList.toggle(bgActive, isActive);
      t.classList.toggle(textActive, isActive);
      t.classList.toggle(bgInactive, !isActive);
      t.classList.toggle(textInactive, !isActive);
      if (t.tagName === 'BUTTON') t.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (t.tagName === 'A') t.setAttribute('aria-current', isActive ? 'page' : 'false');
    });

    const isFiltered = filter !== 'all';

    sections.forEach((section) => {
      const sectionCat = section.getAttribute('data-product-section');
      const show = filter === 'all' || sectionCat === filter;
      section.style.display = show ? '' : 'none';
      section.classList.toggle('is-filtered', show && isFiltered);
      section.querySelectorAll<any>('swiper-container').forEach((swiperEl) => {
        if (swiperEl.swiper) {
          if (show && isFiltered) {
            swiperEl.swiper.disable();
          } else if (show) {
            swiperEl.swiper.enable();
            swiperEl.swiper.update();
          } else {
            swiperEl.swiper.disable();
          }
        }
      });
    });
  }

  const initiallyActive = Array.from(tabs).find(
    (t) => t.getAttribute('aria-pressed') === 'true' || t.getAttribute('aria-current') === 'page',
  );
  applyFilter(initiallyActive ? (initiallyActive.getAttribute('data-category-filter') ?? 'all') : 'all');

  tabs.forEach((tab) => {
    tab.addEventListener('click', function (e) {
      const filter = (this as HTMLElement).getAttribute('data-category-filter') ?? 'all';
      if ((this as HTMLElement).tagName === 'A') e.preventDefault();
      applyFilter(filter);
    });
  });
}

// Init server-rendered before/after sliders (outside Vue template)
function initBeforeAfterSliders() {
  document.querySelectorAll('[data-before-after-slider]').forEach((container) => {
    const handle = container.querySelector('[data-before-after-handle]');
    const afterImg = container.querySelector('.first-step-hero__img--after');
    const dividerLine = container.querySelector('.first-step-hero__divider-line');
    if (!handle || !afterImg) return;
    let position = 50;
    const setPosition = (p: number) => {
      position = Math.max(5, Math.min(95, p));
      (handle as HTMLElement).style.left = `${position}%`;
      if (dividerLine) (dividerLine as HTMLElement).style.left = `${position}%`;
      handle.setAttribute('aria-valuenow', String(Math.round(position)));
      (afterImg as HTMLElement).style.clipPath = `inset(0 0 0 ${position}%)`;
    };
    const onMove = (e: Event) => {
      const ev = e as MouseEvent | TouchEvent;
      const x = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
      const rect = container.getBoundingClientRect();
      setPosition(((x - rect.left) / rect.width) * 100);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      onMove(e);
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });
    handle.addEventListener('touchstart', (e) => {
      e.preventDefault();
      onMove(e);
      document.addEventListener('touchmove', onMove);
      document.addEventListener('touchend', onUp);
    });
    handle.addEventListener('keydown', (e) => {
      const step = (e as KeyboardEvent).shiftKey ? 10 : 5;
      if ((e as KeyboardEvent).key === 'ArrowLeft') setPosition(position - step);
      else if ((e as KeyboardEvent).key === 'ArrowRight') setPosition(position + step);
    });
  });
}

const app = createApp({
  components: {
    QuizRoot,
    QuizDevRoot,
    BaseDialog,
    BaseDialogV2,
    BaseAccordion,
    BaseAccordionRedesign,
    BaseAccordionItem,
    BaseSticky,
    BaseHeader,
    BaseSelect,
    ConsentBanner,
    CommunicationPreferences,
    EmailPopup,
    MainLogo,
    ToastNotifications,
    LoginModal,
    LoginModalQuiz,
    LoginPwaModal,
    ResetPasswordModal,
    ResetPasswordModalQuiz,
    ProductModal,
    ProductModalQuiz,
    QuizIconCard,
    NavigationMenu,
    BaseMenu,
    BaseMasonry,
    CheckoutSuccessListener,
    MasonryWall,
    PromoCode,
    AuthPage,
    FirstStepHero,
    OffsetGrid,
    CardSuccesses,
    SiteHeader,
    SiteHero,
    PricingApproachSection,
    PricingCta1Section,
    PricingCta2Section,
    PricingFaqSection,
    PricingPlansSection,
    PricingVoiceSection,
    PricingWhyJrnysSection,
    BeliefsSection,
    CenteredStatementSection,
    VisionSection,
    AdvantagesSection,
    FirstSection,
    LastSection,
  },
  data() {
    return {
      modals: {},
      modalPayload: {},
      faqCategory: null,
      isNav: false,
      articleTopic: { label: 'All topics', value: '' },
    };
  },
  computed: {
    ...mapStores(useUserStore, useQuizStore),
    role() {
      return getLegacyUserMenuRoleKey(this.userStore.info);
    },
    userMenuItems(): MenuItem[] {
      return getUserMenuItemsForSession(this.userStore.info);
    },
  },
  created() {
    const searchParams = new URLSearchParams(location.search);
    const aid = searchParams.get('aid');
    const cid = searchParams.get('cid');
    const campaign = searchParams.get('utm_campaign');
    const utmName = searchParams.get('utm_name');
    const utmSource = searchParams.get('utm_source');
    const utmMedium = searchParams.get('utm_medium');
    const utmTerm = searchParams.get('utm_term');
    const utmContent = searchParams.get('utm_content');
    const fbc = searchParams.get('fbclid');
    const efTransactionId = searchParams.get('_ef_transaction_id') ?? localStorage.getItem('_ef_transaction_id');
    const gclid = searchParams.get('gclid');
    const gbraid = searchParams.get('gbraid');
    const fbpCookies = document.cookie
      .split(';')
      .filter((c) => c.includes('_fbp='))
      .map((c) => c.split('_fbp=')[1]);
    const fbp = (fbpCookies.length && fbpCookies[0]) || null;

    aid && localStorage.setItem('aid', aid);
    cid && localStorage.setItem('cid', cid);
    campaign && localStorage.setItem('campaign', campaign);
    utmName && localStorage.setItem('utmName', utmName);
    utmSource && localStorage.setItem('utmSource', utmSource);
    utmMedium && localStorage.setItem('utmMedium', utmMedium);
    utmTerm && localStorage.setItem('utmTerm', utmTerm);
    utmContent && localStorage.setItem('utmContent', utmContent);
    fbc && localStorage.setItem('fbc', fbc);
    fbp && localStorage.setItem('fbp', fbp);
    efTransactionId && localStorage.setItem('_ef_transaction_id', efTransactionId);
    gclid && localStorage.setItem('gclid', gclid);
    gbraid && localStorage.setItem('gbraid', gbraid);
  },
  async mounted() {
    this.faqCategory = this.faqCategory ?? 1;

    const response = await this.userStore.getInfo();
    const csrfTokenValue = response?.data?.csrfTokenValue;

    if (csrfTokenValue) {
      const csrfInputs = document.querySelectorAll('input[name="CRAFT_CSRF_TOKEN"]');
      csrfInputs.forEach((input) => {
        (input as HTMLInputElement).value = csrfTokenValue;
      });
    }

    // Identify logged-in users at the start of every session / page load
    if (response?.data && !response.data.isGuest) {
      try {
        await this.userStore.refreshUserProfile();
        this.userStore.identifyUser();
      } catch (e) {
        // Non-blocking — still identify with session-only traits if profile fails
        this.userStore.identifyUser();
      }
    }
    if (window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone) {
      if (window.matchMedia('(max-width: 768px)').matches) {
        if (this.userStore.info?.isGuest) {
          if (this.getItemWithExpiration('skipPwaLogin') === null
            ? true
            : !this.getItemWithExpiration('skipPwaLogin')
          ) {
            this.setModal(MODAL_TYPE.LOGIN_PWA, true);
            this.setItemWithExpiration('skipPwaLogin', '0', 600);
          }
        }
      }
    }

    const campaign = localStorage.getItem('campaign');
    const utmSource = localStorage.getItem('utmSource');
    const utmMedium = localStorage.getItem('utmMedium');
    const fbc = localStorage.getItem('fbc');
    const fbp = localStorage.getItem('fbp');
    const efTransactionId = localStorage.getItem('_ef_transaction_id');
    if (this.userStore.info?.isPatient && (campaign || fbc || fbp || efTransactionId || utmSource || utmMedium)) {
      void apiUser.updateMeta({
        fbc,
        fbp,
        campaign,
        efTransactionId,
        utmSource,
        utmMedium,
      }).catch(() => {});
    }

    // NOTE: $title is consumed by PostHog, name is consumed by sGTM (and
    // technically also PostHog, but as a custom property rather than
    // a standard one)
    void apiAnalytics.trackPageView({
      url: window.location.pathname,
      $title: document.title,
      name: document.title,
    }).catch(() => {});

    // Certain events are tracked by attaching them to a window property,
    // the reason being, among other reasons, due to a race-condition
    // that can result in the CSRF token not being included in the request
    // header.  Anyway, we process said events here
    const analyticsStore = useAnalyticsStore();
    analyticsStore.processTrackEvents();

    initSwiperNavigationButtonVisibility();
    initProductSwiperDragGuard();
    initProductSwiperDirectionGuard();
    initProductSwiperManualSwipeFallback();
    initProductSwiperWheelScroll();
  },
  methods: {
    setModal(type: MODAL_TYPE, visible: boolean, payload?: any) {
      this.modals = {};
      this.modals[type] = visible;
      this.modalPayload[type] = payload;
    },
    goToSignIn() {
      window.location.assign(AUTH_ROUTES.LOGIN);
    },
    /** Use from quiz / deep flows so a successful login can send the user back. */
    goToSignInWithReturn() {
      const path = window.location.pathname.replace(/\/+$/, '') || '/';
      if (path === AUTH_ROUTES.LOGIN) {
        window.location.assign(AUTH_ROUTES.LOGIN);
        return;
      }
      const full = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const u = new URL(AUTH_ROUTES.LOGIN, window.location.origin);
      u.searchParams.set('return', full);
      window.location.assign(u.pathname + u.search);
    },
    goToSignUp() {
      window.location.assign(AUTH_ROUTES.SIGN_UP);
    },
    /** Opens forgot-password on login (drawer); preserves ?return= when applicable. */
    goToForgotPassword() {
      const path = window.location.pathname.replace(/\/+$/, '') || '/';
      if (path === AUTH_ROUTES.FORGOT_PASSWORD) {
        window.location.assign(`${AUTH_ROUTES.FORGOT_PASSWORD}${window.location.search}`);
        return;
      }
      const existingReturn = getSafeLoginReturnUrl();
      const returnTarget = existingReturn
        ?? `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const next = new URL(AUTH_ROUTES.FORGOT_PASSWORD, window.location.origin);
      next.searchParams.set('return', returnTarget);
      window.location.assign(next.pathname + next.search);
    },
    setItemWithExpiration(key: string, value: string, expirationSeconds: number) {
      const expirationMs = expirationSeconds * 1000;
      const expirationTime = Date.now() + expirationMs;
      const item = { value: value, expiration: expirationTime };
      localStorage.setItem(key, JSON.stringify(item));
    },
    getItemWithExpiration(key: string) {
      const localStorageItem: string | null = localStorage.getItem(key);
      if (!localStorageItem) return null;

      const item = JSON.parse(localStorageItem);
      if (Date.now() > item.expiration) {
        localStorage.removeItem(key);
        return null;
      }
      return item.value;
    },
    toggleNav() {
      setTimeout(() => {
        this.isNav = !this.isNav;
      }, 0);
    },
    clickOutsideNav() {
      if (this.isNav) {
        this.isNav = false;
      }
    },
    async redirectToAccount() {
      this.setModal(MODAL_TYPE.LOGIN, false);

      await this.userStore.getInfo();
      location.assign(getPostLoginRedirectUrl(this.userStore.info));
    },
  },
});

// eslint-disable-next-line no-console
app.config.errorHandler = (err) => console.error(err);

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_JS_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
});

app.use(createPinia());
app.use(vClickOutside);
app.use(MasonryWall);
app.use(ToastPlugin);

const root = app.mount('#app');
app.config.globalProperties.root = root;

// Init server-rendered before/after sliders (outside Vue template)
initBeforeAfterSliders();
initProductCategoriesTabs();
initTrustArc();
